'use client'

import { useMandatoryContext } from '@repo/hooks'
import { createContext, PropsWithChildren } from 'react'

import {
  type FeatureFlags,
  type TxSource,
  type TxSourceFlow,
  getTxSource,
  isActionFlowEnabled
} from '@/lib/shared/utils/flags.constants'

const FeatureFlagsContext = createContext<FeatureFlags | null>(null)
FeatureFlagsContext.displayName = 'FeatureFlags'

export function useFeatureFlags(): FeatureFlags {
  return useMandatoryContext(FeatureFlagsContext, 'FeatureFlags')
}

/**
 * Whether a flow's dialog entry point should mount its ActionFlow version, per
 * its `tx*` flag.
 *
 * @public Used once the vault flows mount their ActionFlow versions.
 */
export function useIsActionFlowEnabled(flow: TxSourceFlow): boolean {
  return isActionFlowEnabled(useFeatureFlags(), flow)
}

/**
 * Where a flow's transaction comes from, per its `tx*` flag.
 *
 * @public Used once the flows move to the Earn & Borrow API.
 */
export function useTxSource(flow: TxSourceFlow): TxSource {
  return getTxSource(useFeatureFlags(), flow)
}

export interface FeatureFlagsProviderProps extends PropsWithChildren {
  flags: FeatureFlags
}

export function FeatureFlagsProvider({ children, flags }: FeatureFlagsProviderProps) {
  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
}
